/**
 * Overview home page, /dashboard (§2.11; screenshots/67 advocacy, 69 resident, 70 office, 68 customize, 74 interactions,
 * 55 phone 3). Paints from snapshot:overview (hydrated in main.tsx before render), so nothing animates on first paint.
 *
 * Header: "Good morning, Alex" + Live, the group's sub line, My layout / Team layout (team accounts), Customize.
 * Customize (O1): the toolbar swaps to Saved · Undo · Reset to team layout · Done, the grid columns tint in, and the
 * 400px library drawer pushes the page left. ⌘Z / ⌘⇧Z undo and redo (50 steps), Esc deselects.
 */
import { useEffect, useState } from "react";
import { ConnectionStatus } from "../components/shared/ConnectionStatus";
import type { OrgRole, UserGroup } from "../nav/navConfig";
import { GROUPS } from "../nav/navConfig";
import { Grid } from "./OverviewGrid";
import { OvIcon } from "./icons";
import { Library } from "./Library";
import { MobileOverview } from "./MobileOverview";
import { overviewLayout, overviewStore, useOverview, useOverviewLayout } from "./overviewStore";
import { groupDefault } from "./defaults";
import { ChartEditorHost } from "./ChartEditor";
import { toastStore } from "../stores/toastStore";
import { compact, placeItem } from "./grid/layoutEngine";
import { crossTargets, setOverviewFilter, useOverviewFilter } from "./chartEditorStore";
import { entryOf } from "./catalog";

const hello = (d = new Date()) => (d.getHours() < 12 ? "Good morning" : d.getHours() < 18 ? "Good afternoon" : "Good evening");

export function OverviewPage({ group, role }: { group: UserGroup; role: OrgRole | null }) {
  const snapshot = useOverview((s) => s.snapshot);
  const customizing = useOverviewLayout((s) => s.customizing);
  const scope = useOverview((s) => s.scope);
  const empty = useOverviewLayout((s) => s.layout.length === 0);
  const [drawer, setDrawer] = useState(false);
  const team = GROUPS[group].account === "team" && !!(snapshot?.layouts.team || snapshot?.viewer.canPublish);

  // The library drawer pushes the page (68): the shell's main column makes room for it.
  useEffect(() => {
    document.documentElement.classList.toggle("ov-drawer-open", customizing && drawer);
    return () => document.documentElement.classList.remove("ov-drawer-open");
  }, [customizing, drawer]);

  // 90: a save that met someone else's edits merged them; say so, with Undo when one of my widgets won a tie.
  useEffect(() => overviewLayout.subscribe((s) => s.lastMerge, (m) => {
    if (!m) return;
    const msg = overviewLayout.getState().message;
    toastStore.getState().show({ message: msg, ...(m.theirs.length ? { actionLabel: "Undo", onAction: () => overviewLayout.getState().apply((l) => {
      let out = l;
      for (const t of m.theirs) out = placeItem(out, t.id, t);
      return compact(out);
    }, m.conflicts, `${m.by}’s ${m.conflicts.length === 1 ? "move" : "moves"} applied`) } : {}) });
  }), []);

  useEffect(() => {
    if (!customizing) return;
    const k = (e: KeyboardEvent) => {
      const s = overviewLayout.getState();
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") { e.preventDefault(); if (e.shiftKey) s.redo(); else s.undo(); }
      else if (e.key === "Escape" && !(e.target as HTMLElement).closest?.(".ov-widget, input")) overviewStore.getState().select(null);
    };
    addEventListener("keydown", k);
    return () => removeEventListener("keydown", k);
  }, [customizing]);

  if (!snapshot) return null;
  const name = snapshot.greeting.name;
  const start = () => { overviewLayout.getState().setCustomizing(true); setDrawer(true); };
  const done = () => { overviewLayout.getState().setCustomizing(false); overviewStore.getState().select(null); setDrawer(false); };

  return (
    <div className={`ov-page${customizing ? " is-customizing" : ""}`}>
      <header className="page-header ov-head">
        <div className="page-header__title">
          <div className="title-row">
            <h1 className="page-title">{hello()}{name ? `, ${name}` : ""}</h1>
            <ConnectionStatus />
          </div>
          <p className="page-subtitle">{customizing ? "Customizing" : snapshot.greeting.sub}</p>
        </div>
        {customizing ? <CustomizeToolbar hasTeam={!!snapshot.layouts.team} onDone={done} /> : (
          <div className="ov-head__actions" key="view">
            {team && (
              <span className="ov-switch ov-switch--layout" role="radiogroup" aria-label="Layout">
                {(scope === "team" ? (["team", "mine"] as const) : (["mine", "team"] as const)).map((v) => (
                  <button key={v} role="radio" aria-checked={scope === v} onClick={() => overviewStore.getState().setScope(v)}>{v === "mine" ? "My layout" : "Team layout"}</button>
                ))}
              </span>
            )}
            <button className="btn btn--secondary ov-customize" onClick={start}><OvIcon name="layout-dashboard" />Customize</button>
          </div>
        )}
      </header>
      {customizing && <Presence />}
      <TeamUpdated />
      <CrossFilterBar />
      {empty && !customizing ? <StartOverview group={group} onBlank={start} /> : <Grid group={group} />}
      <MobileOverview hello={hello()} />
      {customizing && drawer && <Library group={group} role={role} onClose={() => setDrawer(false)} />}
      <ChartEditorHost />
    </div>
  );
}

function CustomizeToolbar({ hasTeam, onDone }: { hasTeam: boolean; onDone: () => void }) {
  const save = useOverviewLayout((s) => s.status);
  const canUndo = useOverviewLayout((s) => s.past.length > 0);
  return (
    <div className="ov-head__actions ov-toolbar" key="customize">
      <span className="ov-save" role="status" data-state={save}>
        {save === "saving" ? <><OvIcon name="loader" className="ov-save__spin" />Saving…</> : save === "error" ? <>Not saved</> : <><OvIcon name="cloud-check" />Saved</>}
      </span>
      <button className="ov-icon-btn" aria-label="Undo" disabled={!canUndo} onClick={() => overviewLayout.getState().undo()}><OvIcon name="undo" size={16} /></button>
      <button className="btn btn--secondary" onClick={() => { if (confirm(hasTeam ? "Reset to team layout?" : "Reset?")) overviewStore.getState().reset(); }}>
        <OvIcon name="rotate-ccw" />{hasTeam ? "Reset to team layout" : "Reset"}
      </button>
      <button className="btn btn--primary" onClick={onDone}><OvIcon name="check" />Done</button>
    </div>
  );
}

/** 74 #10: a member sees "Your team updated the overview" with Keep mine / Use team's when the team layout changed. */
function TeamUpdated() {
  const snap = useOverview((s) => s.snapshot);
  const scope = useOverview((s) => s.scope);
  const [open, setOpen] = useState(() => {
    const t = snap?.layouts.team?.updatedAt;
    return !!t && !!snap?.layouts.mine && scope === "mine" && !snap.viewer.canPublish && (!snap.layouts.teamSeenAt || snap.layouts.teamSeenAt < t);
  });
  if (!open || !snap?.layouts.team) return null;
  const choose = (useTeam: boolean) => {
    setOpen(false);
    void fetch("/api/me/overview", { method: "PUT", credentials: "include", headers: { "content-type": "application/json" },
      body: JSON.stringify({ teamSeenAt: snap.layouts.team!.updatedAt, ...(useTeam ? { active: "team" } : {}) }) }).catch(() => {});
    if (useTeam) overviewStore.getState().setScope("team");
  };
  return (
    <div className="ov-team-toast" role="status">
      <span>Your team updated the overview</span>
      <button onClick={() => choose(false)}>Keep mine</button>
      <button className="is-strong" onClick={() => choose(true)}>Use team's</button>
    </div>
  );
}

/** 74 #11: a layout with no widgets starts from the group's defaults, or blank. */
function StartOverview({ group, onBlank }: { group: UserGroup; onBlank: () => void }) {
  const label = { advocacy: "Use advocacy defaults", office: "Use office defaults", resident: "Use resident defaults" }[group];
  return (
    <div className="ov-start">
      <OvIcon name="layout-dashboard" size={20} />
      <h2>Start your overview</h2>
      <div className="ov-start__btns">
        <button className="btn btn--primary" onClick={() => { const d = groupDefault(group); overviewLayout.getState().apply(() => d, d.map((i) => i.id)); }}><OvIcon name="sparkles" />{label}</button>
        <button className="btn btn--secondary" onClick={onBlank}><span className="ov-start__box" aria-hidden />Blank</button>
      </div>
    </div>
  );
}

/**
 * 89 Cross-filter: the filter shows at the top of the page, not hidden in one widget: "Filtered by Samantha Nugent from
 * CAA money · 3 widgets updated", with Esc / Clear.
 */
export function CrossFilterBar() {
  const f = useOverviewFilter();
  const items = useOverviewLayout((s) => s.layout);
  if (!f) return null;
  const src = items.find((i) => i.id === f.sourceWidgetId);
  const n = crossTargets(items, f.field, f.sourceWidgetId).length;
  return (
    <div className="ov-filterbar" role="status">
      <OvIcon name="funnel" size={15} />
      <strong>Filtered by {f.label}</strong>
      <span>{src ? `from ${src.config.title ?? src.config.settings?.labels.title ?? entryOf(src.type).title} · ` : ""}{n} widget{n === 1 ? "" : "s"} updated</span>
      <kbd>Esc</kbd>
      <button type="button" onClick={() => setOverviewFilter(null)}>Clear</button>
    </div>
  );
}

/** 90: "Jordan is also editing · moved 2 widgets" while both edit (org channel presence). */
export function Presence() {
  const p = useOverview((s) => s.presence);
  if (!p) return null;
  const n = p.touched.length;
  return (
    <div className="ov-presence" role="status">
      <OvIcon name="users" size={15} /><span>{p.name} is also editing{n ? ` · moved ${n} widget${n === 1 ? "" : "s"}` : ""}</span>
    </div>
  );
}
